// src/pages/Settings.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Sun, Moon, Clock, Info, RadioButton, Globe } from "@phosphor-icons/react";
import PageTitle from "../components/ui/PageTitle";
import { useThemeMode } from "../providers/AppThemeProvider";
import { usePrayerMode } from "../providers/PrayerModeProvider";

type Dir = "rtl" | "ltr";
const T = {
  title: { ar: "الإعدادات", en: "Settings" },
  theme: {
    ar: { light: "الوضع النهاري", dark: "الوضع الليلي", hint: "اضغط للتبديل" },
    en: { light: "Light Mode", dark: "Dark Mode", hint: "Tap to toggle" },
  },
  prayer: { ar: { title: "طريقة المواقيت", shia: "شيعي", sunni: "سني" }, en: { title: "Prayer Method", shia: "Shia", sunni: "Sunni" } },
  language: { ar: { title: "اللغة", hint: "تبديل لغة التطبيق" }, en: { title: "Language", hint: "Switch app language" } },
  about: { ar: "حول التطبيق", en: "About App" },
  modal: { ar: "اختر طريقة المواقيت", en: "Choose Prayer Method" },
  cancel: { ar: "إلغاء", en: "Cancel" },
  shiaMethod: { ar: "المذهب الشيعي", en: "Shia School" },
  sunniMethod: { ar: "المذهب السني", en: "Sunni School" },
};

export default function Settings() {
  const { mode: prayerMode, setMode: setPrayerMode } = usePrayerMode();
  const { mode: themeMode, toggle: toggleTheme } = useThemeMode();

  const [dir, setDir] = useState<Dir>((document.documentElement.getAttribute("dir") as Dir) || "rtl");
  const lang = dir === "rtl" ? "ar" : "en";
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    localStorage.setItem("appDir", dir);
  }, [dir]);

  useEffect(() => {
    const saved = localStorage.getItem("appDir") as Dir | null;
    if (saved === "rtl" || saved === "ltr") setDir(saved);
  }, []);

  const isDark = themeMode === "dark";

  return (
    <Container>
      <PageTitle icon={<Sun size={28} color="var(--primary)" weight="bold" />}>{T.title[lang]}</PageTitle>

      <SettingsList>
        <SettingItem onClick={toggleTheme}>
          {isDark ? <Sun size={26} color="var(--primary)" weight="bold" /> : <Moon size={26} color="var(--primary)" weight="bold" />}
          <SettingText>
            <strong>{isDark ? T.theme[lang].light : T.theme[lang].dark}</strong>
            <small>{T.theme[lang].hint}</small>
          </SettingText>
          <Switch $on={isDark} />
        </SettingItem>

        <SettingItem onClick={() => setPickerOpen(true)}>
          <Clock size={26} color="var(--primary)" weight="bold" />
          <SettingText>
            <strong>{T.prayer[lang].title}</strong>
            <small>{prayerMode === "shia" ? T.prayer[lang].shia : T.prayer[lang].sunni}</small>
          </SettingText>
        </SettingItem>

        <SettingItem as="div">
          <Globe size={26} color="var(--primary)" weight="bold" />
          <SettingText>
            <strong>{T.language[lang].title}</strong>
            <small>{T.language[lang].hint}</small>
          </SettingText>
          <LangSelect value={dir} onChange={(e) => setDir(e.target.value as Dir)} aria-label="Language">
            <option value="rtl">العربية</option>
            <option value="ltr">English</option>
          </LangSelect>
        </SettingItem>

        <SettingLink to="/about">
          <SettingItem>
            <Info size={26} color="var(--primary)" weight="bold" />
            <SettingText>
              <strong>{T.about[lang]}</strong>
            </SettingText>
          </SettingItem>
        </SettingLink>
      </SettingsList>

      {pickerOpen && (
        <>
          <Scrim onClick={() => setPickerOpen(false)} />
          <Sheet>
            <h3>{T.modal[lang]}</h3>
            <Option onClick={() => { setPrayerMode("shia"); setPickerOpen(false); }}>
              <span>{T.shiaMethod[lang]}</span>
              <RadioButton size={28} weight={prayerMode === "shia" ? "fill" : "regular"} color={prayerMode === "shia" ? "var(--primary)" : "var(--text)"} />
            </Option>
            <Divider />
            <Option onClick={() => { setPrayerMode("sunni"); setPickerOpen(false); }}>
              <span>{T.sunniMethod[lang]}</span>
              <RadioButton size={28} weight={prayerMode === "sunni" ? "fill" : "regular"} color={prayerMode === "sunni" ? "var(--primary)" : "var(--text)"} />
            </Option>
            <CancelBtn onClick={() => setPickerOpen(false)}>{T.cancel[lang]}</CancelBtn>
          </Sheet>
        </>
      )}
    </Container>
  );
}

/* styles */
const Container = styled.div`
  padding: 24px 16px;
  max-width: 960px;
  margin: 0 auto;
`;
const SettingsList = styled.div`margin-top:24px; display:flex; flex-direction:column; gap:16px;`;
const SettingItem = styled.div`
  display:flex; align-items:center; gap:16px;
  background:var(--surface); border-radius:12px; padding:16px; box-shadow:var(--shadow); cursor:pointer;
  &:hover{ background:var(--surface-hover, rgba(0,0,0,.04));}
`;
const SettingLink = styled(Link)`text-decoration:none; color:inherit;`;
const SettingText = styled.div`
  flex:1; display:flex; flex-direction:column;
  strong{ font-size:1rem; color:var(--text); font-weight:600;}
  small{ font-size:.85rem; color:var(--textSecondary);}
`;
const Switch = styled.div<{ $on:boolean }>`
  width:40px; height:22px; border-radius:999px; background:${p=>p.$on?"var(--primary)":"var(--border)"}; position:relative;
  &::after{content:""; position:absolute; top:2px; left:${p=>p.$on?"20px":"2px"}; width:18px; height:18px; border-radius:50%; background:#fff; transition:left .2s;}
`;
const LangSelect = styled.select`border:1px solid var(--border); background:var(--surface); color:var(--text); border-radius:10px; padding:6px 10px;`;
const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: #0006;
  z-index: 1000; /* above sidebar */
`;

const Sheet = styled.div`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 24px;
  background: var(--surface);
  border-radius: 16px;
  padding: 16px;
  z-index: 1001; /* above scrim */
  box-shadow: 0 10px 24px rgba(0,0,0,0.28);

  h3 {
    margin: 0 0 12px;
    text-align: center;
    color: var(--text);
  }

  /* Desktop: center + smaller width */
  @media (min-width: 1024px) {
    left: 50%;
    right: auto;
    bottom: auto;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 380px;                  /* <- smaller */
    max-width: calc(100vw - 64px); /* safety */
    padding: 20px;
    border-radius: 20px;
  }
`;

const Option = styled.button`
  width:100%; display:flex; align-items:center; justify-content:space-between; padding:12px 8px; border:0; background:transparent; cursor:pointer; color:var(--text);
`;
const Divider = styled.div`height:1px; background:var(--border); margin:8px 0;`;
const CancelBtn = styled.button`margin-top:12px; width:100%; padding:10px; border-radius:10px; border:1px solid var(--border); background:transparent; color:var(--textSecondary); cursor:pointer;`;
